import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { toast } from "sonner";
import {
  ArrowLeft,
  MapPin,
  Users,
  Plus,
  Video,
  Edit,
  Download,
  Activity,
  Image,
  FolderOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PhotoGallery from "@/components/PhotoGallery";
import ActivityLog from "@/components/ActivityLog";
import DocumentVault from "@/components/DocumentVault";
import { TabsContent } from "@/components/ui/tabs";
import { formatCurrency } from "@/lib/format";
import { OverviewTab } from "@/components/project/OverviewTab";
import { ChecklistTab } from "@/components/project/ChecklistTab";
import { MeetingsTab } from "@/components/project/MeetingsTab";
import { RemarksTab } from "@/components/project/RemarksTab";
import { NotesTab } from "@/components/project/NotesTab";
import { DocumentsTab } from "@/components/project/DocumentsTab";
import { ScheduleVisitModal, ScheduleMeetingModal, RescheduleMeetingModal } from "@/components/project/ScheduleModals";
import { AddNoteModal, SendSummaryModal } from "@/components/project/NoteModals";
import { AddRemarkModal, SendEmailModal, EditProjectModal, UploadDocumentModal } from "@/components/project/ProjectEditModals";

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditMeetingModal, setShowEditMeetingModal] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [checklist, setChecklist] = useState(null);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDocType, setUploadDocType] = useState("contract");
  const [sendToClient, setSendToClient] = useState(false);
  const [projectNotes, setProjectNotes] = useState([]);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteForm, setNoteForm] = useState({
    note_type: "general_note",
    title: "",
    content: "",
    reference: "",
    notify_client: false,
    notify_manager: false
  });
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryEvent, setSummaryEvent] = useState(null);
  const [summaryForm, setSummaryForm] = useState({
    summary: "",
    decisions: "",
    action_items: "",
    next_steps: "",
    attendees: "",
    duration: "",
    send_to_client: true,
    send_to_manager: false
  });

  const [visitForm, setVisitForm] = useState({ scheduled_date: "", scheduled_time: "", purpose: "", notes: "", send_alerts: true, alert_client: true, alert_team: true, alert_founder: false });
  const [meetingForm, setMeetingForm] = useState({ meeting_type: "online", scheduled_date: "", scheduled_time: "", agenda: "", attendees: "", location: "", send_alerts: true, alert_client: true, alert_team: true, alert_founder: false });
  const [remarkForm, setRemarkForm] = useState({ content: "", remark_type: "general" });
  const [emailForm, setEmailForm] = useState({ template_type: "", subject: "", custom_message: "" });
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchProject();
    checkEmailStatus();
    fetchChecklist();
    fetchProjectNotes();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      setProject(response.data);
      setEditForm({
        final_sqft: response.data.final_sqft || "",
        final_agreed_amount: response.data.final_agreed_amount || "",
        discount_amount: response.data.discount_amount || 0,
        gst_percentage: response.data.gst_percentage || 18
      });
    } catch (error) {
      console.error("Failed to fetch project:", error);
      toast.error("Failed to load project details");
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectNotes = async () => {
    try {
      const response = await apiClient.get(`/project-notes?project_id=${projectId}`);
      setProjectNotes(response.data);
    } catch (error) {
      console.error("Failed to fetch project notes:", error);
    }
  };

  const fetchChecklist = async () => {
    setChecklistLoading(true);
    try {
      const response = await apiClient.get(`/projects/${projectId}/checklist`);
      setChecklist(response.data);
    } catch (error) {
      console.error("Failed to fetch checklist:", error);
    } finally {
      setChecklistLoading(false);
    }
  };

  const handleChecklistItemUpdate = async (itemId, newStatus) => {
    try {
      await apiClient.put(`/projects/${projectId}/checklist/${itemId}?status=${newStatus}`);
      toast.success("Checklist updated");
      fetchChecklist();
    } catch (error) {
      toast.error("Failed to update checklist");
    }
  };

  const handleResetChecklist = async () => {
    if (!window.confirm("Reset checklist to default? This will clear all progress.")) return;
    try {
      await apiClient.post(`/projects/${projectId}/checklist/reset`);
      toast.success("Checklist reset");
      fetchChecklist();
    } catch (error) {
      toast.error("Failed to reset checklist");
    }
  };

  const downloadCalendar = async (type, id) => {
    try {
      const response = await apiClient.get(`/calendar/${type}/${id}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_${id}.ics`;
      a.click();
      toast.success("Calendar file downloaded! Open it to add to your calendar.");
    } catch (error) {
      toast.error("Failed to download calendar file");
    }
  };

  const checkEmailStatus = async () => {
    try {
      const response = await apiClient.get("/email/status");
      setEmailConfigured(response.data.configured);
    } catch (error) {
      console.error("Failed to check email status:", error);
    }
  };

  const handleStageUpdate = async (stageNumber, newStatus) => {
    try {
      await apiClient.put(`/projects/${projectId}/stages`, {
        stage_number: stageNumber,
        status: newStatus,
        start_date: newStatus === 'in_progress' ? new Date().toISOString().split('T')[0] : undefined,
        end_date: newStatus === 'completed' ? new Date().toISOString().split('T')[0] : undefined
      });
      toast.success("Stage updated successfully");
      fetchProject();
    } catch (error) {
      toast.error("Failed to update stage");
    }
  };

  const handlePaymentUpdate = async (paymentId, newStatus) => {
    try {
      await apiClient.put(`/payments/${paymentId}`, {
        status: newStatus,
        paid_date: newStatus === 'paid' ? new Date().toISOString().split('T')[0] : undefined
      });
      toast.success("Payment updated successfully");
      fetchProject();
    } catch (error) {
      toast.error("Failed to update payment");
    }
  };

  const handleScheduleVisit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await apiClient.post("/site-visits", { project_id: projectId, ...visitForm });
      const notified = response.data.notifications_sent || [];
      if (notified.length > 0) {
        const recipients = notified.map(n => n.recipient).join(", ");
        toast.success(`Site visit scheduled! Alerts sent to: ${recipients}`);
      } else {
        toast.success("Site visit scheduled successfully!");
      }
      setShowVisitModal(false);
      setVisitForm({ scheduled_date: "", scheduled_time: "", purpose: "", notes: "", send_alerts: true, alert_client: true, alert_team: true, alert_founder: false });
      fetchProject();
    } catch (error) {
      toast.error("Failed to schedule visit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleScheduleMeeting = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await apiClient.post("/meetings", {
        project_id: projectId,
        ...meetingForm,
        attendees: meetingForm.attendees.split(",").map(a => a.trim()).filter(Boolean)
      });
      const notified = response.data.notifications_sent || [];
      if (notified.length > 0) {
        const recipients = notified.map(n => n.recipient).join(", ");
        toast.success(`Meeting scheduled! Alerts sent to: ${recipients}`);
      } else {
        toast.success("Meeting scheduled successfully!");
      }
      setShowMeetingModal(false);
      setMeetingForm({ meeting_type: "online", scheduled_date: "", scheduled_time: "", agenda: "", attendees: "", location: "", send_alerts: true, alert_client: true, alert_team: true, alert_founder: false });
      fetchProject();
    } catch (error) {
      toast.error("Failed to schedule meeting");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditMeeting = (meeting) => {
    setEditingMeeting(meeting);
    setMeetingForm({
      meeting_type: meeting.meeting_type,
      scheduled_date: meeting.scheduled_date,
      scheduled_time: meeting.scheduled_time,
      agenda: meeting.agenda,
      attendees: meeting.attendees?.join(", ") || "",
      location: meeting.location || "",
      send_alerts: false,
      alert_client: false,
      alert_team: false,
      alert_founder: false
    });
    setShowEditMeetingModal(true);
  };

  const handleUpdateMeeting = async (e) => {
    e.preventDefault();
    if (!editingMeeting) return;
    setSubmitting(true);
    try {
      await apiClient.put(`/meetings/${editingMeeting.meeting_id}`, {
        scheduled_date: meetingForm.scheduled_date,
        scheduled_time: meetingForm.scheduled_time,
        send_alerts: meetingForm.send_alerts,
        alert_client: meetingForm.alert_client,
        alert_team: meetingForm.alert_team
      });
      toast.success(meetingForm.send_alerts ? "Meeting rescheduled & alerts sent!" : "Meeting updated!");
      setShowEditMeetingModal(false);
      setEditingMeeting(null);
      setMeetingForm({ meeting_type: "online", scheduled_date: "", scheduled_time: "", agenda: "", attendees: "", location: "", send_alerts: true, alert_client: true, alert_team: true, alert_founder: false });
      fetchProject();
    } catch (error) {
      toast.error("Failed to update meeting");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddRemark = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post("/remarks", { project_id: projectId, ...remarkForm });
      toast.success("Remark added successfully!");
      setShowRemarkModal(false);
      setRemarkForm({ content: "", remark_type: "general" });
      fetchProject();
    } catch (error) {
      toast.error("Failed to add remark");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadFile(file);
    setShowUploadModal(true);
  };

  const handleConfirmUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("project_id", projectId);
    formData.append("document_type", uploadDocType);
    formData.append("name", uploadFile.name);
    formData.append("send_to_client", sendToClient);

    try {
      const response = await apiClient.post("/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (sendToClient && response.data.email_sent) {
        toast.success("Document uploaded & sent to client!");
      } else if (sendToClient) {
        toast.success("Document uploaded! (Email sending failed)");
      } else {
        toast.success("Document uploaded successfully!");
      }
      fetchProject();
      setShowUploadModal(false);
      setUploadFile(null);
      setSendToClient(false);
      setUploadDocType("contract");
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!project?.client) return;
    setSubmitting(true);
    try {
      await apiClient.post("/email/send", {
        recipient_email: project.client.email,
        recipient_name: project.client.name,
        subject: emailForm.subject,
        template_type: emailForm.template_type,
        project_id: projectId,
        custom_message: emailForm.custom_message
      });
      toast.success("Email sent successfully!");
      setShowEmailModal(false);
    } catch (error) {
      toast.error("Failed to send email");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.put(`/projects/${projectId}`, editForm);
      toast.success("Project updated successfully!");
      setShowEditModal(false);
      fetchProject();
    } catch (error) {
      toast.error("Failed to update project");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateNote = async () => {
    if (!noteForm.title.trim() || !noteForm.content.trim()) {
      toast.error("Please enter title and content");
      return;
    }
    setSubmitting(true);
    try {
      const response = await apiClient.post("/project-notes", {
        project_id: projectId,
        ...noteForm
      });

      const notificationMsg = response.data.notifications_sent?.length > 0
        ? ` Notifications sent to: ${response.data.notifications_sent.map(n => n.recipient).join(", ")}`
        : "";

      toast.success(`Note created successfully!${notificationMsg}`);
      setShowNoteModal(false);
      setNoteForm({
        note_type: "general_note",
        title: "",
        content: "",
        reference: "",
        notify_client: false,
        notify_manager: false
      });
      fetchProjectNotes();
    } catch (error) {
      toast.error("Failed to create note");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;
    try {
      await apiClient.delete(`/project-notes/${noteId}`);
      toast.success("Note deleted");
      fetchProjectNotes();
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  const openSummaryModal = (event, eventType) => {
    setSummaryEvent({ ...event, event_type: eventType });
    setSummaryForm({
      summary: "",
      decisions: "",
      action_items: "",
      next_steps: "",
      attendees: "",
      duration: "",
      send_to_client: true,
      send_to_manager: false
    });
    setShowSummaryModal(true);
  };

  const handleSendSummary = async () => {
    if (!summaryForm.summary.trim()) {
      toast.error("Please enter a summary of the discussion");
      return;
    }

    setSubmitting(true);
    try {
      const actionItems = summaryForm.action_items.trim()
        ? summaryForm.action_items.split('\n').filter(item => item.trim())
        : null;

      const attendees = summaryForm.attendees.trim()
        ? summaryForm.attendees.split(',').map(a => a.trim()).filter(a => a)
        : null;

      const response = await apiClient.post("/send-summary", {
        event_type: summaryEvent.event_type,
        event_id: summaryEvent.event_type === "meeting" ? summaryEvent.meeting_id : summaryEvent.visit_id,
        summary: summaryForm.summary,
        decisions: summaryForm.decisions || null,
        action_items: actionItems,
        next_steps: summaryForm.next_steps || null,
        attendees: attendees,
        duration: summaryForm.duration || null,
        send_to_client: summaryForm.send_to_client,
        send_to_manager: summaryForm.send_to_manager
      });

      const notifCount = response.data.notifications_sent?.length || 0;
      toast.success(`Summary sent to ${notifCount} recipient(s) and saved to notes`);
      setShowSummaryModal(false);
      fetchProject();
      fetchProjectNotes();
    } catch (error) {
      console.error("Error sending summary:", error);
      toast.error("Failed to send summary");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/download/project/${projectId}/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Project_${project.name.replace(/ /g, '_')}_Report.pdf`;
      a.click();
      toast.success("PDF downloaded!");
    } catch (err) {
      toast.error("Failed to download PDF");
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="space-y-6">
          <div className="h-8 skeleton w-1/4"></div>
          <div className="h-96 skeleton"></div>
        </div>
      </div>
    );
  }

  if (!project) return null;

  const totalPaid = project.payments?.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0) || 0;
  const totalPending = project.payments?.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0) || 0;

  return (
    <div className="page-content animate-fade-in" data-testid="project-detail">
      {/* Header */}
      <div className="mb-6">
        <Link to="/projects" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors group">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Projects
        </Link>

        <div className="flex items-start justify-between">
          <div className="animate-slide-in-left">
            <h1 className="text-2xl font-bold text-white">{project.name}</h1>
            <div className="flex items-center gap-4 mt-2">
              <Link to={`/clients/${project.client_id}`} className="text-gray-400 hover:text-[#FF4500] flex items-center gap-1 transition-colors">
                <Users className="w-4 h-4" />
                {project.client?.name}
              </Link>
              <span className="text-gray-600">•</span>
              <span className="text-gray-400 flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {project.site_location}
              </span>
            </div>
            <div className="flex gap-2 mt-3">
              <span className={`badge type-${project.project_type}`}>{project.project_type}</span>
              <span className={`badge ${project.status === 'active' ? 'status-active' : 'status-pending'}`}>{project.status}</span>
            </div>
          </div>

          <div className="flex gap-2 animate-slide-in-right">
            <Button onClick={handleDownloadPdf} variant="outline" className="border-[#262626] hover:bg-[#262626]" data-testid="download-pdf-btn">
              <Download className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Button onClick={() => setShowEditModal(true)} variant="outline" className="border-[#262626] hover:bg-[#262626]" data-testid="edit-project">
              <Edit className="w-4 h-4 mr-2" /> Edit
            </Button>
            <Button onClick={() => setShowMeetingModal(true)} variant="outline" className="border-[#262626] hover:bg-[#262626]">
              <Video className="w-4 h-4 mr-2" /> Meeting
            </Button>
            <Button onClick={() => setShowVisitModal(true)} className="bg-[#FF4500] hover:bg-[#E65100] btn-glow" data-testid="schedule-visit-btn">
              <Plus className="w-4 h-4 mr-2" /> Site Visit
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4 mb-8">
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-4 card-hover animate-fade-in-up stagger-1">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Area</p>
          <p className="text-xl font-bold text-white">{project.final_sqft || project.approximate_sqft} sqft</p>
          {!project.final_sqft && <p className="text-xs text-[#F59E0B]">Approx.</p>}
        </div>
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-4 card-hover animate-fade-in-up stagger-2">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Rate/Sqft</p>
          <p className="text-xl font-bold text-white">{formatCurrency(project.rate_per_sqft)}</p>
        </div>
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-4 card-hover animate-fade-in-up stagger-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Agreed Amount</p>
          <p className="text-xl font-bold text-[#FF4500]">{formatCurrency(project.final_agreed_amount)}</p>
          <p className="text-xs text-gray-500">+GST {formatCurrency(project.gst_amount)}</p>
        </div>
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-4 card-hover animate-fade-in-up stagger-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Received</p>
          <p className="text-xl font-bold text-[#10B981]">{formatCurrency(totalPaid)}</p>
        </div>
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-4 card-hover animate-fade-in-up stagger-5">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Pending</p>
          <p className="text-xl font-bold text-[#F59E0B]">{formatCurrency(totalPending)}</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#1A1A1A] border border-[#262626] p-1 flex-wrap">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-xs sm:text-sm">Overview</TabsTrigger>
          <TabsTrigger value="activity" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-xs sm:text-sm">
            <Activity className="w-3 h-3 mr-1 hidden sm:inline" />Activity
          </TabsTrigger>
          <TabsTrigger value="gallery" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-xs sm:text-sm">
            <Image className="w-3 h-3 mr-1 hidden sm:inline" />Gallery
          </TabsTrigger>
          <TabsTrigger value="vault" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-xs sm:text-sm">
            <FolderOpen className="w-3 h-3 mr-1 hidden sm:inline" />Vault
          </TabsTrigger>
          <TabsTrigger value="checklist" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-xs sm:text-sm">
            Checklist {checklist?.progress?.percent > 0 && <span className="ml-1 px-1.5 py-0.5 rounded bg-white/20 text-[10px]">{checklist.progress.percent}%</span>}
          </TabsTrigger>
          <TabsTrigger value="meetings" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-xs sm:text-sm">
            Meetings {project.meetings?.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded bg-white/20 text-[10px]">{project.meetings.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="remarks" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-xs sm:text-sm">
            Remarks {project.remarks?.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded bg-white/20 text-[10px]">{project.remarks.length}</span>}
          </TabsTrigger>
        </TabsList>

        <OverviewTab
          project={project}
          emailConfigured={emailConfigured}
          handleStageUpdate={handleStageUpdate}
          handlePaymentUpdate={handlePaymentUpdate}
          onRemindPayment={() => { setEmailForm({ template_type: "payment_reminder", subject: `Payment Reminder - ${project.name}`, custom_message: "" }); setShowEmailModal(true); }}
          onScheduleVisit={() => setShowVisitModal(true)}
        />

        {/* Activity Log Tab */}
        <TabsContent value="activity" className="animate-fade-in">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-[#FF4500]" /> Project Activity Log
            </h3>
            <ActivityLog projectId={projectId} />
          </div>
        </TabsContent>

        {/* Photo Gallery Tab */}
        <TabsContent value="gallery" className="animate-fade-in">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Image className="w-5 h-5 text-[#FF4500]" /> Photo Gallery
            </h3>
            <PhotoGallery projectId={projectId} />
          </div>
        </TabsContent>

        {/* Document Vault Tab */}
        <TabsContent value="vault" className="animate-fade-in">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <FolderOpen className="w-5 h-5 text-[#FF4500]" /> Document Vault
            </h3>
            <DocumentVault projectId={projectId} />
          </div>
        </TabsContent>

        <ChecklistTab
          checklist={checklist}
          checklistLoading={checklistLoading}
          handleChecklistItemUpdate={handleChecklistItemUpdate}
          handleResetChecklist={handleResetChecklist}
        />

        <MeetingsTab
          project={project}
          onScheduleMeeting={() => setShowMeetingModal(true)}
          handleEditMeeting={handleEditMeeting}
          downloadCalendar={downloadCalendar}
          openSummaryModal={openSummaryModal}
        />

        <RemarksTab project={project} onAddRemark={() => setShowRemarkModal(true)} />

        <NotesTab
          projectNotes={projectNotes}
          onAddNote={() => setShowNoteModal(true)}
          handleDeleteNote={handleDeleteNote}
        />

        <DocumentsTab
          project={project}
          fileInputRef={fileInputRef}
          handleFileUpload={handleFileUpload}
          uploading={uploading}
        />
      </Tabs>

      <ScheduleVisitModal
        open={showVisitModal}
        onOpenChange={setShowVisitModal}
        project={project}
        visitForm={visitForm}
        setVisitForm={setVisitForm}
        handleScheduleVisit={handleScheduleVisit}
        submitting={submitting}
      />

      <ScheduleMeetingModal
        open={showMeetingModal}
        onOpenChange={setShowMeetingModal}
        project={project}
        meetingForm={meetingForm}
        setMeetingForm={setMeetingForm}
        handleScheduleMeeting={handleScheduleMeeting}
        submitting={submitting}
      />

      <RescheduleMeetingModal
        open={showEditMeetingModal}
        onOpenChange={setShowEditMeetingModal}
        editingMeeting={editingMeeting}
        setEditingMeeting={setEditingMeeting}
        meetingForm={meetingForm}
        setMeetingForm={setMeetingForm}
        handleUpdateMeeting={handleUpdateMeeting}
        submitting={submitting}
      />

      <AddRemarkModal
        open={showRemarkModal}
        onOpenChange={setShowRemarkModal}
        remarkForm={remarkForm}
        setRemarkForm={setRemarkForm}
        handleAddRemark={handleAddRemark}
        submitting={submitting}
      />

      <SendEmailModal
        open={showEmailModal}
        onOpenChange={setShowEmailModal}
        project={project}
        emailForm={emailForm}
        setEmailForm={setEmailForm}
        handleSendEmail={handleSendEmail}
        submitting={submitting}
      />

      <EditProjectModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        editForm={editForm}
        setEditForm={setEditForm}
        handleUpdateProject={handleUpdateProject}
        submitting={submitting}
      />

      <UploadDocumentModal
        open={showUploadModal}
        onOpenChange={setShowUploadModal}
        project={project}
        uploadFile={uploadFile}
        setUploadFile={setUploadFile}
        uploadDocType={uploadDocType}
        setUploadDocType={setUploadDocType}
        sendToClient={sendToClient}
        setSendToClient={setSendToClient}
        handleConfirmUpload={handleConfirmUpload}
        uploading={uploading}
      />

      <AddNoteModal
        open={showNoteModal}
        onOpenChange={setShowNoteModal}
        project={project}
        noteForm={noteForm}
        setNoteForm={setNoteForm}
        handleCreateNote={handleCreateNote}
        submitting={submitting}
      />

      <SendSummaryModal
        open={showSummaryModal}
        onOpenChange={setShowSummaryModal}
        project={project}
        summaryEvent={summaryEvent}
        summaryForm={summaryForm}
        setSummaryForm={setSummaryForm}
        handleSendSummary={handleSendSummary}
        submitting={submitting}
      />
    </div>
  );
}
